import { Icon } from 'antd';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { memo, useCallback, useState } from 'react';
import EditButton from '../../../EditButton/EditButton';
import ExpandButton from '../../../ExpandButton/ExpandButton';
import styles from './LayeredTableRow.module.scss';

const LayeredTableRow = ({
  officeData,
  rowData,
  expandable = false,
  editable = false,
  deletable = false,
  rowDataPropertiesArray,
  expandedRowContent,
  clickHighlighted,
  lightTheme,
  rowOnClick,
  onDeleteRow,
  onEditRow,
  isEditing,
  columnsCount,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const clickRow = (e) => {
    if (expandable) {
      setIsExpanded(!isExpanded);
    }
    rowOnClick && rowOnClick(rowData, e);
  };
  const clickEdit = useCallback(
    (e) => {
      e.stopPropagation();
      onEditRow(!isEditing, rowData, officeData);
    },
    [isEditing, onEditRow, rowData, officeData]
  );

  const clickDelete = useCallback(
    (e) => {
      e.stopPropagation();
      onDeleteRow(rowData, officeData);
    },
    [onDeleteRow, officeData, rowData]
  );

  const getCellContent = (data, propName, index) => {
    let fieldValue = propName.split('.').reduce((object, key) => {
      if (typeof object === 'object') {
        if (Array.isArray(object[key])) {
          return object[key][0];
        }
        return object[key];
      } else {
        return '-';
      }
    }, data);

    if (rowDataPropertiesArray[index]?.highlight) {
      return (
        <div
          onClick={
            typeof clickHighlighted === 'function'
              ? () => clickHighlighted(data)
              : undefined
          }
          className={styles.blockHighlight}
        >
          {fieldValue}
        </div>
      );
    } else {
      return fieldValue;
    }
  };

  return (
    <>
      <div
        key={`layered-table${rowData[rowDataPropertiesArray[0]]}`}
        className={classNames(styles.layeredTableRow, {
          [styles.expanded]: isExpanded,
          [styles.allRowClickable]: !!rowOnClick,
        })}
        onClick={clickRow}
      >
        {expandable === 'start' && (
          <div
            key={`expand`}
            className={classNames(styles.tableCell, styles.shortCell)}
          >
            <ExpandButton isExpanded={isExpanded} lightTheme={lightTheme} />
          </div>
        )}

        {rowDataPropertiesArray.map((prop, index) => (
          <div key={`${index}`} className={styles.tableCell}>
            {prop.customCellContent ? (
              <div
                onClick={
                  typeof clickHighlighted === 'function'
                    ? () => clickHighlighted(rowData)
                    : undefined
                }
                className={prop.highlight && styles.blockHighlight}
              >
                {prop.customCellContent(rowData)}
              </div>
            ) : (
              getCellContent(rowData, prop.name, index)
            )}
          </div>
        ))}
        {editable && (
          <div
            key={`edit`}
            className={classNames(styles.tableCell, styles.button)}
          >
            <EditButton isEditing={isEditing} clickEdit={clickEdit} />
          </div>
        )}
        {deletable && (
          <div
            key={`delete`}
            className={classNames(styles.tableCell, styles.button)}
          >
            <Icon
              onClick={clickDelete}
              type="delete"
              className={classNames(styles.deleteButton, styles.nonEditMode)}
            />
          </div>
        )}
        {expandable === 'end' && (
          <div
            key={`expand`}
            className={classNames(styles.tableCell, styles.button)}
          >
            <ExpandButton isExpanded={isExpanded} lightTheme={lightTheme} />
          </div>
        )}
      </div>
      {isExpanded && (
        <div
          className={styles.expandedRow}
          style={{ gridColumnEnd: `span ${columnsCount}` }}
        >
          {expandedRowContent(rowData)}
        </div>
      )}
    </>
  );
};

LayeredTableRow.propTypes = {
  officeData: PropTypes.object, // на случай, когда нужно отрисовать данные помещения при открытии модалки
  rowData: PropTypes.object.isRequired,
  rowClassName: PropTypes.string,
  expandable: PropTypes.oneOf(['start', 'end', false]), // либо кнопка расширения ряда находится в начале ряда, либо в конце, либо ее нет
  rowDataPropertiesArray: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      flex: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        .isRequired,
      highlight: PropTypes.bool,
    })
  ).isRequired, // перечислить ключи обьекта, значения которого нарисуются в ряду
  expandedRowContent: PropTypes.elementType,
  expandButtonFlex: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  rowOnClick: PropTypes.func, // онклик на ряд
  lightTheme: PropTypes.bool,
  clickHighlighted: PropTypes.func, // при клике на выделенные голубые имена помещений/объектов и тд
};

export default memo(LayeredTableRow);
